import React, { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import StarOutlineOutlinedIcon from "@mui/icons-material/StarOutlineOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PermMediaOutlinedIcon from "@mui/icons-material/PermMediaOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import Sidebarlogo from "../assests/sidebarLogo.png";
import "./SideBar.css";
import { useSelector } from "react-redux";

const BLOG_ITEMS = [
  { label: "Dashboard",  path: "/blog",            Icon: DashboardOutlinedIcon },
  { label: "Posts",      path: "/blog/posts",      Icon: ArticleOutlinedIcon },
  { label: "Categories", path: "/blog/categories", Icon: CategoryOutlinedIcon },
  { label: "Tags",       path: "/blog/tags",       Icon: LocalOfferOutlinedIcon },
  { label: "Comments",   path: "/blog/comments",   Icon: CommentOutlinedIcon },
  { label: "Media",      path: "/blog/media",      Icon: PermMediaOutlinedIcon },
  { label: "Analytics",  path: "/blog/analytics",  Icon: InsightsOutlinedIcon },
  { label: "Redirects",  path: "/blog/redirects",  Icon: CompareArrowsOutlinedIcon },
  { label: "Activity",   path: "/blog/activity",   Icon: AccessTimeOutlinedIcon },
];

function NavItem({ to, icon: Icon, label, active, onClick }) {
  return (
    <Link to={to} className={`nav-item${active ? " nav-item--active" : ""}`} onClick={onClick}>
      <Icon className="nav-item__icon" />
      <span className="nav-item__label">{label}</span>
    </Link>
  );
}

function SideBar({ openSidebarToggle, OpenSidebar, isMdOrAbove, onActiveLinkChange }) {
  const user = useSelector((state) => state?.user?.user);
  const permissions = user?.permissions || {};
  const location = useLocation();
  const currentPath = location.pathname;

  const [blogOpen, setBlogOpen] = useState(false);
  const sidebarRef = useRef();

  useEffect(() => {
    const path = location.pathname.split("/").filter((p) => p)[0];
    onActiveLinkChange(path);
    if (location.pathname.startsWith("/blog")) setBlogOpen(true);
  }, [location, onActiveLinkChange]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target) && !e.target.closest("#navbar-toggle")) {
        OpenSidebar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [OpenSidebar]);

  const close = () => OpenSidebar(false);
  const isActive = (path) => currentPath === path || (path !== "/blog" && currentPath.startsWith(path));
  const hasPermission = (p) => user?.role === 3 || permissions[p] !== false;

  const mobileOpen = !isMdOrAbove && openSidebarToggle;

  return (
    <>
      {mobileOpen && (
        <div className="sidebar-backdrop" onClick={() => OpenSidebar(false)} />
      )}
    <aside
      ref={sidebarRef}
      id="sidebar"
      className={`sidebar-responsive${mobileOpen ? " sidebar-mobile-open" : ""}`}
    >
      <div className="sidebar-logo">
        <img src={Sidebarlogo} alt="logo" />
      </div>

      <nav className="sidebar-nav">

        {hasPermission("dashboard") && (
          <NavItem to="/dashboard" icon={DashboardOutlinedIcon} label="Dashboard" active={isActive("/dashboard")} onClick={close} />
        )}

        {hasPermission("userManagement") && (
          <NavItem to="/user-management" icon={PeopleOutlinedIcon} label="User Management" active={isActive("/user-management")} onClick={close} />
        )}

        {hasPermission("vendorManagement") && (
          <NavItem to="/vender-management" icon={StorefrontOutlinedIcon} label="Vendor Management" active={isActive("/vender-management")} onClick={close} />
        )}

        {hasPermission("orders") && (
          <NavItem to="/all-order" icon={ShoppingBagOutlinedIcon} label="Orders" active={isActive("/all-order")} onClick={close} />
        )}

        {hasPermission("contentModeration") && (
          <NavItem to="/content-moderation" icon={GavelOutlinedIcon} label="Content Moderation" active={isActive("/content-moderation")} onClick={close} />
        )}

        {hasPermission("reportAndAnalysis") && (
          <NavItem to="/report-analysis" icon={BarChartOutlinedIcon} label="Report & Analysis" active={isActive("/report-analysis")} onClick={close} />
        )}

        {hasPermission("eventManagement") && (
          <NavItem to="/event-management" icon={EventOutlinedIcon} label="Event Management" active={isActive("/event-management")} onClick={close} />
        )}

        {hasPermission("pushNotification") && (
          <NavItem to="/push-notification" icon={NotificationsOutlinedIcon} label="Push Notification" active={isActive("/push-notification")} onClick={close} />
        )}

        {hasPermission("helpAndSupport") && (
          <NavItem to="/help-support" icon={HelpOutlineOutlinedIcon} label="Help & Support" active={isActive("/help-support")} onClick={close} />
        )}

        {hasPermission("feedbacks") && (
          <NavItem to="/feedbacks" icon={StarOutlineOutlinedIcon} label="Feedbacks" active={isActive("/feedbacks")} onClick={close} />
        )}

        {hasPermission("seo") && (
          <NavItem to="/seo" icon={TravelExploreOutlinedIcon} label="SEO" active={isActive("/seo")} onClick={close} />
        )}

        {hasPermission("blog") && (
          <div className="nav-group">
            <button
              className={`nav-item nav-item--trigger${currentPath.startsWith("/blog") ? " nav-item--active" : ""}`}
              onClick={() => setBlogOpen((o) => !o)}
            >
              <ArticleOutlinedIcon className="nav-item__icon" />
              <span className="nav-item__label">Blog</span>
              <ExpandMoreIcon className={`nav-item__chevron${blogOpen ? " nav-item__chevron--open" : ""}`} />
            </button>

            <div className={`nav-group__sub${blogOpen ? " nav-group__sub--open" : ""}`}>
              {BLOG_ITEMS.map(({ label, path, Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={`nav-sub-item${isActive(path) ? " nav-sub-item--active" : ""}`}
                  onClick={close}
                >
                  <Icon className="nav-sub-item__icon" />
                  <span className="nav-sub-item__label">{label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

      </nav>
    </aside>
    </>
  );
}

export default SideBar;
